import { useCallback, useEffect, useState } from "preact/hooks"
import { toCamelCase } from "@helpers/toCamelCase"
import { updateSearchProvidersWebsiteSearchParams } from "@stores/providersWebsiteSearchParams"
import { useTopicEventSubscribe } from "@client/nanostores/useTopicEventSubscribe"

const API_URL = "/api/providers-website"

export interface WebsiteProvider {
  object: "provider"
  id: string
  seq: number
  slug: string
  name: string
  title: string
  displayName: string
  ratingScore: string
  ratingsCount: number
  profileImage: string
  profileImage120X120: string
  profileImage200X200: string
  coverImage: string
  profileImageAssetName: string | null
  specialty: string
  yearsOfExperience: number
  licenseNumber: string
  education: string
  residency: string | null
  previousEmployer: string
  summary: string
  bio: string
  boardCertified: boolean
  circleMedicalRole: string | null
  isUniquePerson: boolean
  hasProfile: boolean
  badges: string[]
  limitedAvailability: boolean
  highlights: Highlight[]
  languages: string[]
  youtubeEmbed: string | null
  profileVideo: ProfileVideo | null
  licenseStates: string[]
  licenseEnrollments: LicenseEnrollment[]
  isEnrolledInInsurance: boolean | null
}

export interface Highlight {
  description: string
  icon: string
}

export interface ProfileVideo {
  thumbnail: string
  contentHls: string
}

export interface LicenseEnrollment {
  stateCode: string
  payers: Payer[]
}

export interface Payer {
  name: string
  effectiveOn: string
}

export const useProviders = ({
  stateCode,
  limit = 20,
  page = 1,
}: {
  stateCode?: string
  limit?: number
  page?: number
}) => {
  const [websiteProviders, setWebsiteProviders] = useState<WebsiteProvider[]>(
    [],
  )
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    updateSearchProvidersWebsiteSearchParams({ stateCode })
  }, [stateCode])

  const fetchProviders = useCallback(async () => {
    if (!stateCode) return

    setIsLoading(true)

    const params = new URLSearchParams()
    if (stateCode) params.set("state_code", stateCode)
    if (limit) params.set("limit", limit.toString())
    if (page) params.set("page", page.toString())

    const response = await fetch(`${API_URL}?${params.toString()}`)
    const data = await response.json()

    const transformedData = toCamelCase<WebsiteProvider[]>(data)
    setWebsiteProviders(transformedData)
    setIsLoading(false)
  }, [stateCode, limit, page])

  useEffect(() => {
    fetchProviders()
  }, [fetchProviders])

  useTopicEventSubscribe("website-providers:load", fetchProviders)

  return { websiteProviders, isLoading }
}
